import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner, Collapse, InputGroup, Pagination } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSave, FaTimes, FaUpload, FaMagic, FaExternalLinkAlt } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AppNavbar from './AppNavbar';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import { error as logError } from '../utils/logger';
import '../styles/App.css';
import { apiUrl, authUrl, assetUrl } from '../utils/apiUrl';


const featuredImageUrl = (url) => assetUrl(url);

const QUILL_MODULES = (imageHandler) => ({
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      [{ color: [] }, { background: [] }],
      ['clean']
    ],
    handlers: imageHandler ? { image: imageHandler } : {}
  }
});

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'image',
  'blockquote', 'code-block', 'color', 'background'
];

const BlogAdmin = () => {
  const navigate = useNavigate();
  const { language, loading: languageLoading } = useLanguage();
  // Asegurar que siempre tengamos un idioma válido
  const currentLanguage = language || 'es';
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' = all, 'true' = published, 'false' = draft
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const applySearch = () => {
    setSearchQuery(searchInput);
    setPage(1);
  };
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'movie',
    content: '',
    excerpt: '',
    featured_image: '',
    published: false,
    published_at: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    language: 'es',
    related_title: '',
    related_year: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [uploadingEditorImage, setUploadingEditorImage] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateTopic, setGenerateTopic] = useState('');
  const [generateLanguage, setGenerateLanguage] = useState('es');
  const [generating, setGenerating] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const [lastPromptSent, setLastPromptSent] = useState(null);
  const [showPromptSent, setShowPromptSent] = useState(false);
  const quillRef = useRef(null);
  const featuredFileInputRef = useRef(null);

  const handleEditorImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingEditorImage(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        if (formData.title) fd.append('title', formData.title);
        if (editingPost?.slug) fd.append('slug', editingPost.slug);
        const res = await fetch(apiUrl('/api/blog/upload'), {
          method: 'POST',
          credentials: 'include',
          body: fd
        });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        const editor = quillRef.current?.getEditor?.();
        if (editor && data.url) {
          const range = editor.getSelection(true) || { index: editor.getLength() };
          editor.insertEmbed(range.index, 'image', data.url);
        }
      } catch (err) {
        logError(err);
      } finally {
        setUploadingEditorImage(false);
      }
    };
    input.click();
  }, [formData.title, editingPost?.slug]);

  const quillModules = useMemo(() => QUILL_MODULES(handleEditorImage), [handleEditorImage]);

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFeatured(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (formData.title) fd.append('title', formData.title);
      if (editingPost?.slug) fd.append('slug', editingPost.slug);
      const res = await fetch(apiUrl('/api/blog/upload'), {
        method: 'POST',
        credentials: 'include',
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      if (data.url) setFormData((prev) => ({ ...prev, featured_image: data.url }));
    } catch (err) {
      logError(err);
    } finally {
      setUploadingFeatured(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  useEffect(() => {
    if (user && !checkingAuth) loadPosts();
  }, [page, statusFilter, searchQuery, user, checkingAuth]);

  useEffect(() => {
    if (!accessDenied) return;
    const intervalId = setInterval(() => {
      setRedirectCountdown((s) => {
        if (s <= 1) {
          clearInterval(intervalId);
          navigate('/', { replace: true });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [accessDenied, navigate]);

  const checkAuthAndLoad = async () => {
    try {
      setCheckingAuth(true);
      // Verificar autenticación primero
      const authResponse = await fetch(authUrl('/auth/me'), { credentials: 'include' });
      if (!authResponse.ok || authResponse.status === 401) {
        navigate('/');
        return;
      }
      
      const userData = await authResponse.json();
      if (userData.role !== 'superuser') {
        setAccessDenied(true);
        setCheckingAuth(false);
        setLoading(false);
        return;
      }
      setUser(userData);
      setCheckingAuth(false);
    } catch (err) {
      logError('Error verificando autenticación:', err);
      navigate('/');
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));
      if (statusFilter !== '') params.set('published', statusFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      const response = await fetch(apiUrl(`/api/blog/admin/posts?${params}`), {
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          setAccessDenied(true);
        } else {
          setError(t('common.blog.errorLoading', currentLanguage));
        }
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setPosts(data.posts || []);
      setTotalPosts(data.total != null ? data.total : (data.posts || []).length);
    } catch (err) {
      logError('Error cargando posts:', err);
      setError(t('common.blog.errorLoading', currentLanguage));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!checkingAuth && user) loadPosts();
  }, [page, statusFilter, searchQuery, user, checkingAuth]);

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      category: 'movie',
      content: '',
      excerpt: '',
      featured_image: '',
      published: false,
      published_at: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      language: 'es',
      related_title: '',
      related_year: ''
    });
    setShowModal(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      category: post.category || 'movie',
      content: post.content || '',
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      published: post.published || false,
      published_at: post.published_at ? new Date(post.published_at).toISOString().split('T')[0] : '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      language: post.language === 'en' ? 'en' : 'es',
      related_title: post.related_title || '',
      related_year: post.related_year || ''
    });
    setShowModal(true);
  };

  // URL pública: EN /news/movies/slug, ES /noticias/peliculas/slug
  const getCategoryPath = (category, lang) => {
    const paths = {
      en: { movie: 'movies', series: 'series', music: 'music' },
      es: { movie: 'peliculas', series: 'series', music: 'musica' }
    };
    return paths[lang === 'en' ? 'en' : 'es'][category] || (lang === 'en' ? 'movies' : 'peliculas');
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('common.blog.confirmDelete', currentLanguage))) {
      return;
    }
    
    try {
      const response = await fetch(apiUrl(`/api/blog/admin/posts/${postId}`), {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        alert(t('common.blog.errorDeleting', currentLanguage));
        return;
      }
      
      await loadPosts();
    } catch (err) {
      logError('Error eliminando post:', err);
      alert(t('common.blog.errorDeleting', currentLanguage));
    }
  };

  const handleGenerateWithAI = async () => {
    const topic = generateTopic.trim();
    if (!topic) return;
    setGenerating(true);
    setLastUsage(null);
    try {
      const res = await fetch(apiUrl('/api/blog/admin/generate-post'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ topic, language: generateLanguage })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(payload.error || t('common.blog.generateError', currentLanguage));
        return;
      }
      const { post: createdPost, usage, prompt_sent } = payload;
      setLastUsage(usage || null);
      setLastPromptSent(prompt_sent || null);
      setShowPromptSent(!!prompt_sent);
      setShowGenerateModal(false);
      setGenerateTopic('');
      await loadPosts();
      setFormData({
        title: createdPost.title || '',
        category: createdPost.category || 'movie',
        content: createdPost.content || '',
        excerpt: createdPost.excerpt || '',
        featured_image: createdPost.featured_image || '',
        published: false,
        published_at: '',
        meta_title: createdPost.meta_title || '',
        meta_description: createdPost.meta_description || '',
        meta_keywords: createdPost.meta_keywords || '',
        language: createdPost.language === 'en' ? 'en' : 'es',
        related_title: createdPost.related_title || '',
        related_year: createdPost.related_year || ''
      });
      setEditingPost(createdPost);
      setShowModal(true);
    } catch (err) {
      logError(err);
      alert(t('common.blog.generateError', currentLanguage));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = editingPost
        ? apiUrl(`/api/blog/admin/posts/${editingPost.id}`)
        : apiUrl('/api/blog/admin/posts');
      
      const method = editingPost ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        published_at: formData.published_at || null
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || t('common.blog.errorSaving', currentLanguage));
        return;
      }
      
      setShowModal(false);
      await loadPosts();
    } catch (err) {
      logError('Error guardando post:', err);
      alert(t('common.blog.errorSaving', currentLanguage));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      movie: currentLanguage === 'en' ? 'Movies' : 'Películas',
      series: currentLanguage === 'en' ? 'TV Series' : 'Series',
      music: currentLanguage === 'en' ? 'Music' : 'Música'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      movie: 'primary',
      series: 'success',
      music: 'warning'
    };
    return colors[category] || 'secondary';
  };

  const getPostUrl = (post) => {
    if (!post.published || !post.slug) return null;
    const lang = post.language === 'en' ? 'en' : 'es';
    const base = lang === 'en' ? '/news' : '/noticias';
    const categoryPath = getCategoryPath(post.category, lang);
    return `${base}/${categoryPath}/${post.slug}`;
  };

  const handleLogout = () => {
    setUser(null);
    navigate('/', { replace: true });
  };

  if (accessDenied) {
    return (
      <div className="App d-flex align-items-center justify-content-center min-vh-100 bg-light">
        <Container className="py-5">
          <div className="text-center py-5 px-3">
            <div className="display-1 mb-3" role="img" aria-hidden="true" style={{ lineHeight: 1 }}>
              😅
            </div>
            <p className="text-muted text-uppercase small fw-bold mb-2">
              {t('common.blog.accessDeniedOops', currentLanguage)}
            </p>
            <h1 className="h2 fw-bold mb-3">{t('common.blog.accessDeniedTitle', currentLanguage)}</h1>
            <p className="lead text-muted mb-4 mx-auto" style={{ maxWidth: '420px' }}>
              {t('common.blog.accessDeniedMessage', currentLanguage)}
            </p>
            <p className="small text-muted">
              {tf('common.blog.accessDeniedRedirect', { seconds: String(redirectCountdown) }, currentLanguage)}
            </p>
            <Button variant="outline-primary" className="mt-3" onClick={() => navigate('/', { replace: true })}>
              {t('allNews.backToHome', currentLanguage)}
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (checkingAuth || loading || languageLoading) {
    return (
      <div className="App">
        <div className="py-5">
          <Container>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">{checkingAuth ? t('common.checkingAuth', currentLanguage) : t('common.blog.loading', currentLanguage)}</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <AppNavbar
        user={user}
        onLogout={handleLogout}
        language={language}
        isSuperUser={true}
        aiServiceStatus={false}
        activeSection="blog-admin"
      />
      <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('common.blog.adminTitle', currentLanguage)}</h1>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => { setGenerateTopic(''); setGenerateLanguage(currentLanguage === 'en' ? 'en' : 'es'); setShowGenerateModal(true); }} disabled={generating}>
            <FaMagic className="me-2" />
            {t('common.blog.generateWithAI', currentLanguage)}
          </Button>
          <Button variant="primary" onClick={handleNewPost}>
            <FaPlus className="me-2" />
            {t('common.blog.newPost', currentLanguage)}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={4}>
              <InputGroup>
                <Form.Control
                  type="search"
                  placeholder={t('common.blog.searchPlaceholder', currentLanguage)}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applySearch())}
                />
                <Button variant="outline-secondary" onClick={applySearch}>
                  {t('common.blog.searchButton', currentLanguage)}
                </Button>
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                aria-label={t('common.blog.filterStatus', currentLanguage)}
              >
                <option value="">{t('common.blog.filterStatusAll', currentLanguage)}</option>
                <option value="true">{t('common.blog.filterStatusPublished', currentLanguage)}</option>
                <option value="false">{t('common.blog.filterStatusDraft', currentLanguage)}</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>{t('common.blog.title', currentLanguage)}</th>
                <th>{t('common.blog.category', currentLanguage)}</th>
                <th>{t('common.blog.status', currentLanguage)}</th>
                <th>{t('common.blog.publishedAt', currentLanguage)}</th>
                <th>{t('common.blog.views', currentLanguage)}</th>
                <th>{t('common.blog.actions', currentLanguage)}</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    {t('common.blog.noPosts', currentLanguage)}
                  </td>
                </tr>
              ) : (
                posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <strong>{post.title}</strong>
                      {post.slug && (
                        <div className="small text-muted">/{post.slug}</div>
                      )}
                    </td>
                    <td>
                      <Badge bg={getCategoryColor(post.category)}>
                        {getCategoryLabel(post.category)}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={post.published ? 'success' : 'secondary'}>
                        {post.published ? t('common.blog.published', currentLanguage) : t('common.blog.draft', currentLanguage)}
                      </Badge>
                    </td>
                    <td>{formatDate(post.published_at)}</td>
                    <td>{post.views || 0}</td>
                    <td>
                      <div className="d-flex gap-2">
                        {getPostUrl(post) && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => window.open(getPostUrl(post), '_blank')}
                            title={t('common.blog.viewPost', currentLanguage)}
                          >
                            <FaEye />
                          </Button>
                        )}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditPost(post)}
                          title={t('common.blog.edit', currentLanguage)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeletePost(post.id)}
                          title={t('common.blog.delete', currentLanguage)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {totalPosts > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 pt-2 border-top">
              <span className="small text-muted">
                {tf('common.blog.showingCount', {
                  from: String((page - 1) * PAGE_SIZE + 1),
                  to: String(Math.min(page * PAGE_SIZE, totalPosts)),
                  total: String(totalPosts)
                }, currentLanguage)}
              </span>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={(e) => { e?.preventDefault?.(); setPage((p) => Math.max(1, p - 1)); }}
                >
                  {t('common.blog.prev', currentLanguage)}
                </Pagination.Prev>
                <Pagination.Item disabled>
                  {t('common.blog.page', currentLanguage)} {page} / {Math.max(1, Math.ceil(totalPosts / PAGE_SIZE))}
                </Pagination.Item>
                <Pagination.Next
                  disabled={page >= Math.ceil(totalPosts / PAGE_SIZE)}
                  onClick={(e) => { e?.preventDefault?.(); setPage((p) => p + 1); }}
                >
                  {t('common.blog.next', currentLanguage)}
                </Pagination.Next>
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal Generar con IA */}
      <Modal show={showGenerateModal} onHide={() => !generating && setShowGenerateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaMagic className="me-2" />
            {t('common.blog.generateWithAI', currentLanguage)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('common.blog.generateTopicLabel', currentLanguage)}</Form.Label>
            <Form.Control
              type="text"
              value={generateTopic}
              onChange={(e) => setGenerateTopic(e.target.value)}
              placeholder={t('common.blog.generateTopicPlaceholder', currentLanguage)}
              disabled={generating}
            />
            <Form.Text className="text-muted">
              {t('common.blog.generateTopicHelp', currentLanguage)}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('common.blog.generateLanguageLabel', currentLanguage)}</Form.Label>
            <Form.Select
              value={generateLanguage}
              onChange={(e) => setGenerateLanguage(e.target.value)}
              disabled={generating}
            >
              <option value="es">{t('common.blog.generateLanguageEs', currentLanguage)}</option>
              <option value="en">{t('common.blog.generateLanguageEn', currentLanguage)}</option>
            </Form.Select>
            <Form.Text className="text-muted">
              {t('common.blog.generateLanguageHelp', currentLanguage)}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)} disabled={generating}>
            {t('common.close', currentLanguage)}
          </Button>
          <Button variant="primary" onClick={handleGenerateWithAI} disabled={generating || !generateTopic.trim()}>
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.blog.generating', currentLanguage)}
              </>
            ) : (
              <>
                <FaMagic className="me-2" />
                {t('common.blog.generateWithAI', currentLanguage)}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de edición/creación */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setLastUsage(null); setLastPromptSent(null); setShowPromptSent(false); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPost ? t('common.blog.editPost', currentLanguage) : t('common.blog.newPost', currentLanguage)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {lastUsage && (
            <Alert variant="info" className="mb-3 small">
              <strong>{t('common.blog.usageTitle', currentLanguage)}</strong>
              <ul className="mb-0 mt-1">
                {lastUsage.prompt_tokens != null && (
                  <li>{t('common.blog.usagePromptTokens', currentLanguage)}: {lastUsage.prompt_tokens}</li>
                )}
                {lastUsage.completion_tokens != null && (
                  <li>{t('common.blog.usageCompletionTokens', currentLanguage)}: {lastUsage.completion_tokens}</li>
                )}
                {lastUsage.total_tokens != null && (
                  <li>{t('common.blog.usageTotalTokens', currentLanguage)}: {lastUsage.total_tokens}</li>
                )}
                {lastUsage.model && (
                  <li>{t('common.blog.usageModel', currentLanguage)}: {lastUsage.model}</li>
                )}
                {lastUsage.response_time_ms != null && (
                  <li>{t('common.blog.usageResponseTime', currentLanguage)}: {(lastUsage.response_time_ms / 1000).toFixed(2)} s</li>
                )}
              </ul>
              <p className="mb-0 mt-1 small text-muted">{t('common.blog.generateSuccess', currentLanguage)}</p>
            </Alert>
          )}
          {lastPromptSent && (
            <div className="mb-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPromptSent((v) => !v)}
                aria-expanded={showPromptSent}
              >
                {t('common.blog.promptSentTitle', currentLanguage)}
              </Button>
              <Collapse in={showPromptSent}>
                <div className="mt-2">
                  {lastPromptSent.system && (
                    <div className="mb-2">
                      <strong className="small text-muted">{t('common.blog.promptSentSystem', currentLanguage)}</strong>
                      <pre className="bg-light p-2 rounded small mb-0" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {lastPromptSent.system}
                      </pre>
                    </div>
                  )}
                  {lastPromptSent.user && (
                    <div>
                      <strong className="small text-muted">{t('common.blog.promptSentUser', currentLanguage)}</strong>
                      <pre className="bg-light p-2 rounded small mb-0" style={{ maxHeight: '200px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {lastPromptSent.user}
                      </pre>
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          )}
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.title', currentLanguage)} *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={t('common.blog.titlePlaceholder', currentLanguage)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.category', currentLanguage)} *</Form.Label>
                  <Form.Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="movie">{getCategoryLabel('movie')}</option>
                    <option value="series">{getCategoryLabel('series')}</option>
                    <option value="music">{getCategoryLabel('music')}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.postLanguage', currentLanguage)}</Form.Label>
                  <Form.Select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  >
                    <option value="es">{t('common.blog.generateLanguageEs', currentLanguage)} → /noticias/peliculas/...</option>
                    <option value="en">{t('common.blog.generateLanguageEn', currentLanguage)} → /news/movies/...</option>
                  </Form.Select>
                  <Form.Text className="text-muted">{t('common.blog.postLanguageHelp', currentLanguage)}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.relatedTitleLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.related_title || ''}
                    onChange={(e) => setFormData({ ...formData, related_title: e.target.value })}
                    placeholder={t('common.blog.relatedTitlePlaceholder', currentLanguage)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('common.blog.relatedYearLabel', currentLanguage)}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.related_year || ''}
                    onChange={(e) => setFormData({ ...formData, related_year: e.target.value })}
                    placeholder={t('common.blog.relatedYearPlaceholder', currentLanguage)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.excerpt', currentLanguage)}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder={t('common.blog.excerptPlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.excerptHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.content', currentLanguage)} *</Form.Label>
              <div className="blog-editor-wrapper">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData((prev) => ({ ...prev, content: value }))}
                  modules={quillModules}
                  formats={QUILL_FORMATS}
                  placeholder={t('common.blog.contentPlaceholder', currentLanguage)}
                  style={{ minHeight: 280 }}
                />
                {uploadingEditorImage && (
                  <div className="small text-muted mt-1">{t('common.blog.uploadingImage', currentLanguage)}</div>
                )}
              </div>
              <Form.Text className="text-muted">
                {t('common.blog.contentHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.featuredImage', currentLanguage)}</Form.Label>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <Form.Control
                  type="url"
                  className="flex-grow-1"
                  style={{ maxWidth: 400 }}
                  value={formData.featured_image}
                  onChange={(e) => setFormData((prev) => ({ ...prev, featured_image: e.target.value }))}
                  placeholder={t('common.blog.imageUrlPlaceholder', currentLanguage)}
                />
                <input
                  ref={featuredFileInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleFeaturedImageUpload}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => featuredFileInputRef.current?.click()}
                  disabled={uploadingFeatured}
                >
                  {uploadingFeatured ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {t('common.blog.uploadingImage', currentLanguage)}
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-1" />
                      {t('common.blog.uploadImage', currentLanguage)}
                    </>
                  )}
                </Button>
              </div>
              <Form.Text className="text-muted">
                {t('common.blog.uploadImageHelp', currentLanguage)}
              </Form.Text>
              {(formData.related_title || formData.related_year) && (
                <div className="mt-2">
                  <a
                    href={`https://www.google.com/search?tbm=isch&tbs=isz:l&q=${encodeURIComponent(
                      [formData.related_title, formData.related_year].filter(Boolean).join(' ')
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary"
                  >
                    <FaExternalLinkAlt className="me-1" />
                    {t('common.blog.searchImagesOnGoogle', currentLanguage)}
                  </a>
                </div>
              )}
              {formData.featured_image && (
                <div className="mt-2">
                  <img
                    src={featuredImageUrl(formData.featured_image)}
                    alt="Preview"
                    style={{ maxHeight: 120, borderRadius: 8, border: '1px solid #dee2e6' }}
                  />
                </div>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={t('common.blog.published', currentLanguage)}
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                </Form.Group>
              </Col>
              {formData.published && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('common.blog.publishedAt', currentLanguage)}</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.published_at}
                      onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>

            <hr />

            <h5 className="mb-3">{t('common.blog.seoSettings', currentLanguage)}</h5>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.metaTitle', currentLanguage)}</Form.Label>
              <Form.Control
                type="text"
                value={formData.meta_title}
                onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                placeholder={t('common.blog.metaTitlePlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.metaTitleHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.metaDescription', currentLanguage)}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder={t('common.blog.metaDescriptionPlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.metaDescriptionHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('common.blog.metaKeywords', currentLanguage)}</Form.Label>
              <Form.Control
                type="text"
                value={formData.meta_keywords}
                onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                placeholder={t('common.blog.metaKeywordsPlaceholder', currentLanguage)}
              />
              <Form.Text className="text-muted">
                {t('common.blog.metaKeywordsHelp', currentLanguage)}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
            <FaTimes className="me-2" />
            {t('common.cancel', currentLanguage)}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !formData.title || !formData.content}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.blog.saving', currentLanguage)}
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                {t('common.blog.save', currentLanguage)}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </div>
  );
};

export default BlogAdmin;
